import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { Shift } from '@wilson/interfaces';
import { ShiftMultiSelectHelperService } from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  BehaviorSubject,
  Observable,
  catchError,
  combineLatest,
  map,
  mergeMap,
  of,
  tap,
} from 'rxjs';
import { TimelineCommonOperationsService } from '../../../services/timeline-common-operations.service';
import { TranslateService } from '@ngx-translate/core';
import { NzMessageService } from 'ng-zorro-antd/message';

@Injectable()
export class MultipleShiftsDrawerService {
  private readonly translate = inject(TranslateService);
  private readonly nzMessageService = inject(NzMessageService);
  readonly shiftMultiSelectHelperService = inject(
    ShiftMultiSelectHelperService,
  );
  readonly store = inject(Store);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );

  private _refreshSubject = new BehaviorSubject(0);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoadingSubject.asObservable();

  public selectedShifts$: Observable<((Shift & { id: string }) | null)[]> =
    combineLatest([
      this.shiftMultiSelectHelperService.selectedShifts$,
      this._refreshSubject,
    ]).pipe(
      mergeMap(([shiftIds]) => {
        if (shiftIds.length) {
          this.isLoadingSubject.next(true);
          const shiftObservables = shiftIds.map((shiftPosition) => {
            return this.timelineCommonOperationsService
              .fetchShift([shiftPosition.id])
              .pipe(
                map((shifts) => {
                  return shifts[0] || null;
                }),
                catchError(() => {
                  this.nzMessageService.error(
                    this.translate.instant('general.something_went_wrong'),
                  );
                  this.isLoadingSubject.next(false);
                  return of([] as unknown as Shift & { id: string });
                }),
              );
          });

          return combineLatest(shiftObservables).pipe(
            tap(() => {
              this.isLoadingSubject.next(false);
            }),
          );
        } else {
          return of([]);
        }
      }),
    );

  refreshDrawerData(): void {
    this._refreshSubject.next(Math.random());
  }
}
